import React, { useState } from 'react';
import { Factory, Settings, Search, Ship, ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import EditableText from './EditableText';

const ServicesSection = () => {
  const [expandedCard, setExpandedCard] = useState(null);
  const { t } = useTranslation();

  const iconMap = {
    Factory: Factory,
    Settings: Settings,
    Search: Search,
    Ship: Ship
  };

  const services = [
    {
      id: 1,
      title: t('services.supplierSelection.title'),
      description: t('services.supplierSelection.description'),
      icon: 'Factory',
      field: 'supplierSelection'
    },
    {
      id: 2,
      title: t('services.productDevelopment.title'),
      description: t('services.productDevelopment.description'),
      icon: 'Settings',
      field: 'productDevelopment'
    },
    {
      id: 3,
      title: t('services.qualityControl.title'),
      description: t('services.qualityControl.description'),
      icon: 'Search',
      field: 'qualityControl'
    },
    {
      id: 4,
      title: t('services.logisticsManagement.title'),
      description: t('services.logisticsManagement.description'),
      icon: 'Ship',
      field: 'logisticsManagement'
    }
  ];

  const getServiceDetails = (serviceId) => {
    const detailsMap = {
      1: 'supplierSelection',
      2: 'productDevelopment', 
      3: 'qualityControl',
      4: 'logisticsManagement'
    };
    const key = detailsMap[serviceId];
    return {
      steps: [
        t(`services.${key}.steps.step1`),
        t(`services.${key}.steps.step2`),
        t(`services.${key}.steps.step3`),
        t(`services.${key}.steps.step4`)
      ],
      benefits: [
        t(`services.${key}.benefits.benefit1`),
        t(`services.${key}.benefits.benefit2`),
        t(`services.${key}.benefits.benefit3`)
      ]
    };
  };

  const toggleCard = (id) => {
    setExpandedCard(expandedCard === id ? null : id);
  };

  return (
    <section id="services" className="py-20 lg:py-28 bg-white">
      <div className="container mx-auto px-6 max-w-7xl">
        <div className="text-center mb-16">
          <EditableText
            section="services"
            field="title"
            as="h2"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4"
          >
            {t('services.title')}
          </EditableText>
          <EditableText
            section="services"
            field="subtitle"
            as="p"
            className="text-lg sm:text-xl text-slate-600 max-w-3xl mx-auto"
          >
            {t('services.subtitle')}
          </EditableText>
        </div>

        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {services.map((service) => {
            const Icon = iconMap[service.icon];
            const isExpanded = expandedCard === service.id;
            const details = isExpanded ? getServiceDetails(service.id) : null;

            return (
              <div
                key={service.id}
                className="bg-white rounded-xl p-6 sm:p-8 border border-slate-200 hover:border-slate-300 shadow-sm hover:shadow-md transition-all duration-300"
                data-testid={`service-card-${service.id}`}
              >
                <div className="flex items-start mb-4">
                  <div className="bg-slate-900 p-4 rounded-xl mr-4 flex-shrink-0">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <EditableText
                      section="services"
                      field={`${service.field}Title`}
                      as="h3"
                      className="text-xl sm:text-2xl font-bold text-slate-900 mb-2"
                    >
                      {service.title}
                    </EditableText>
                    <EditableText
                      section="services"
                      field={`${service.field}Description`}
                      as="p"
                      className="text-slate-600 leading-relaxed"
                    >
                      {service.description}
                    </EditableText>
                  </div>
                </div>

                <button
                  onClick={() => toggleCard(service.id)}
                  className="flex items-center text-blue-700 hover:text-blue-900 font-semibold mt-4 transition-colors"
                >
                  <EditableText
                    section="services"
                    field="moreInfoButton"
                    as="span"
                  >
                    {t('services.moreInfo')}
                  </EditableText>
                  {isExpanded ? (
                    <ChevronUp className="w-5 h-5 ml-2" />
                  ) : (
                    <ChevronDown className="w-5 h-5 ml-2" />
                  )}
                </button>

                {isExpanded && details && (
                  <div className="mt-6 pt-6 border-t border-slate-200 animate-fadeIn">
                    <EditableText
                      section="services"
                      field="processTitle"
                      as="h4"
                      className="font-semibold text-slate-900 mb-3"
                    >
                      {t('services.process')}
                    </EditableText>
                    <ol className="list-decimal list-inside space-y-2 mb-6">
                      {details.steps.map((step, index) => (
                        <li key={index} className="text-slate-600">
                          <EditableText
                            section="services"
                            field={`${service.field}Step${index + 1}`}
                            as="span"
                          >
                            {step}
                          </EditableText>
                        </li>
                      ))}
                    </ol>

                    <EditableText
                      section="services"
                      field="benefitsTitle"
                      as="h4"
                      className="font-semibold text-slate-900 mb-3"
                    >
                      {t('services.benefits')}
                    </EditableText>
                    <ul className="list-disc list-inside space-y-2">
                      {details.benefits.map((benefit, index) => (
                        <li key={index} className="text-slate-600">
                          <EditableText
                            section="services"
                            field={`${service.field}Benefit${index + 1}`}
                            as="span"
                          >
                            {benefit}
                          </EditableText>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;